interface RequestConfig {
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  headers?: Record<string, string>;
  body?: unknown;
}

interface ApiResponse<T = unknown> {
  data: T;
  message?: string;
  success: boolean;
}

class HttpClient {
  private baseURL: string;
  private defaultHeaders: Record<string, string>;

  constructor(baseURL: string = process.env.NEXT_PUBLIC_API_ENDPOINT || "") {
    this.baseURL = baseURL;
    this.defaultHeaders = {
      "Content-Type": "application/json",
    };
  }

  private async request<T>(
    endpoint: string,
    config: RequestConfig = {}
  ): Promise<ApiResponse<T>> {
    const { method = "GET", headers = {}, body } = config;

    const url = `${this.baseURL}${endpoint}`;
    
    // Don't stringify FormData, and don't set Content-Type for FormData
    const isFormData = body instanceof FormData;
    const requestHeaders = isFormData
      ? { ...headers }
      : {
          ...this.defaultHeaders,
          ...headers,
        };

    try {
      const response = await fetch(url, {
        method,
        headers: requestHeaders,
        body: isFormData ? body : (body ? JSON.stringify(body) : undefined),
      });

      let data: unknown;
      const contentType = response.headers.get("content-type");

      try {
        if (contentType && contentType.includes("application/json")) {
          data = await response.json();
        } else {
          const text = await response.text();
          data = text ? { message: text } : {};
        }
      } catch (parseError) {
        console.error("Error parsing response:", parseError);
        data = { message: "Failed to parse response" };
      }

      if (!response.ok) {
        const errorData = data as { 
          message?: string; 
          errors?: string[] | { errors?: string[] } | string;
        };
        
        // Extract error message from various structures
        let errorMessage: string | undefined;
        if (errorData.errors) {
          if (Array.isArray(errorData.errors)) {
            errorMessage = errorData.errors[0];
          } else if (typeof errorData.errors === 'object' && errorData.errors.errors && Array.isArray(errorData.errors.errors)) {
            errorMessage = errorData.errors.errors[0];
          } else if (typeof errorData.errors === 'string') {
            errorMessage = errorData.errors;
          }
        }
        
        return {
          data: data as T,
          success: false,
          message:
            errorMessage ||
            errorData.message ||
            `HTTP error! status: ${response.status}`,
        };
      }

      return {
        data: data as T,
        success: true,
      };
    } catch (error) {
      console.error("HTTP request failed:", error);
      throw error;
    }
  }

  async get<T>(
    endpoint: string,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: "GET", headers });
  }

  async post<T>(
    endpoint: string,
    body?: unknown,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: "POST", body, headers });
  }

  async put<T>(
    endpoint: string,
    body?: unknown,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: "PUT", body, headers });
  }

  async delete<T>(
    endpoint: string,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: "DELETE", headers });
  }

  async patch<T>(
    endpoint: string,
    body?: unknown,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: "PATCH", body, headers });
  }

  setAuthToken(token: string) {
    this.defaultHeaders["Authorization"] = `Bearer ${token}`;
  }

  removeAuthToken() {
    delete this.defaultHeaders["Authorization"];
  }
}

// Create and export a default instance
const http = new HttpClient();

if (typeof window !== 'undefined') {
  const token = localStorage.getItem('access_token');
  if (token) http.setAuthToken(token);
}

export default http;
export { HttpClient, type ApiResponse, type RequestConfig };
